import { nanoid } from 'nanoid';
import logger from '@utils/logger';

/**
 * Shape of gRPC-web metadata (BrowserHeaders from @improbable-eng/grpc-web):
 * keys are lower-cased header/trailer names, values are string arrays.
 */
type GrpcMetadataLike = {
  headersMap?: { [key: string]: string[] };
};

/** Trailer/header names the backend may use to echo tracing info back. */
const SERVER_REQUEST_ID_KEYS = ['x-request-id'];
const SERVER_TIME_KEYS = ['x-server-time', 'date'];

const readMetaValue = (meta: GrpcMetadataLike | undefined, keys: string[]): string | undefined => {
  const map = meta?.headersMap;
  if (!map) return undefined;
  for (let i = 0; i < keys.length; i++) {
    const values = map[keys[i]];
    if (values && values.length > 0 && values[0]) {
      return values[0];
    }
  }
  return undefined;
};

/**
 * Correlation id for a single API call. It IS sent to the backend as the
 * `x-request-id` header (Envoy preserves it, every service propagates and
 * logs it, and it is echoed back in response trailers), so the same value
 * correlates console output, error toasts (`ref: ...`), Sentry events on
 * both frontend and backend, and every backend log line of the request.
 */
export const newRequestId = (): string => nanoid(10);

export type ApiErrorLogParams = {
  methodName: string;
  code: number | undefined;
  message: string | undefined;
  /** Client-generated correlation id from `newRequestId()`. */
  requestId?: string;
  /** `performance.now()` captured right before the call started. */
  startedAt?: number;
  /** gRPC response headers/trailers, if the error carries them. */
  grpcMetadata?: GrpcMetadataLike;
};

/**
 * Emits one concise, always-on console line per failed API call so that
 * end-user console screenshots contain enough context to trace the failure
 * in backend logs (request id, server time, gRPC code, duration).
 */
export const logApiError = ({
  methodName,
  code,
  message,
  requestId,
  startedAt,
  grpcMetadata,
}: ApiErrorLogParams): void => {
  const serverRequestId = readMetaValue(grpcMetadata, SERVER_REQUEST_ID_KEYS);
  const serverTime = readMetaValue(grpcMetadata, SERVER_TIME_KEYS);
  const durationMs =
    startedAt !== undefined ? Math.round(performance.now() - startedAt) : undefined;

  // Client-vs-server clock offset: narrows the backend log search window when
  // the user's clock is wrong (a screenshot timestamp can be minutes off).
  const serverTimeMs = serverTime ? Date.parse(serverTime) : NaN;
  const clockSkewMs = Number.isNaN(serverTimeMs)
    ? undefined
    : Math.round(serverTimeMs - Date.now());

  const parts = [
    `${methodName} failed`,
    `code=${code ?? '?'}`,
    requestId ? `reqId=${requestId}` : null,
    serverRequestId && serverRequestId !== requestId ? `serverReqId=${serverRequestId}` : null,
    serverTime ? `serverTime=${serverTime}` : null,
    clockSkewMs !== undefined ? `clockSkew=${clockSkewMs}ms` : null,
    durationMs !== undefined ? `dur=${durationMs}ms` : null,
    message ? `msg="${message}"` : null,
  ].filter(Boolean);

  logger.error('api', parts.join(' '));
};

export default logApiError;
